/**
 * @fileoverview User Workspace Preferences Service
 *
 * Service for managing user-specific workspace preferences including
 * group order and expanded/collapsed state.
 */

import { apiClient } from "./api";
import { cache, CACHE_KEYS } from "../utils/cacheUtils";
import { isOfflineError } from "../utils/connectivity";
import type { ErrorExtras } from "../utils/errorUtils";
import { logger } from "@common/utils/logger";

const PREFERENCES_ENDPOINT = "/preferences";

// Type definitions

/** A group's saved order and expanded state in one workspace. */
export interface GroupPreference {
  group_id: number;
  position: number;
  expanded: boolean;
}

/** `PreferencesRead`: a saved preference, as the API returns it. */
export interface StoredGroupPreference extends GroupPreference {
  user_id?: number;
  workspace_id?: number;
  created_at?: string | null;
  updated_at?: string | null;
}

/** A group with its preference applied. */
export type GroupWithPreferences<T> = T & {
  expanded: boolean;
  position: number;
};

/** The batch PUT's reply. */
export interface PreferencesUpdateResult {
  success?: boolean;
  message?: string;
  preferences?: StoredGroupPreference[];
}

/**
 * Service for managing user workspace preferences
 */
export const preferencesService = {
  /**
   * Get user preferences for a workspace
   */
  async getWorkspacePreferences(
    workspaceId: number,
  ): Promise<StoredGroupPreference[]> {
    try {
      const response = await apiClient.get<StoredGroupPreference[]>(
        `${PREFERENCES_ENDPOINT}/workspace/${workspaceId}`,
      );
      logger.debug(
        "[preferencesService] getWorkspacePreferences response:",
        response,
      );
      // The response IS the data (not response.data). Kept in the user's
      // cache only as the offline copy: online, every read is fresh.
      const preferences = response || [];
      cache.set(CACHE_KEYS.WORKSPACE_PREFERENCES(workspaceId), preferences);
      return preferences;
    } catch (error) {
      // Offline: the last preferences seen for this workspace, so groups
      // keep their order and expanded state.
      if (isOfflineError(error)) {
        return (
          cache.get<StoredGroupPreference[]>(
            CACHE_KEYS.WORKSPACE_PREFERENCES(workspaceId),
            { ttl: Infinity },
          ) ?? []
        );
      }
      logger.error("[preferencesService] Error fetching preferences:", error);
      // Return empty array on error to allow the app to continue
      const status = (error as ErrorExtras).response?.status;
      if (status === 404 || status === 401) {
        return [];
      }
      throw error;
    }
  },

  /**
   * Set preferences for a single group in a workspace
   */
  async setGroupPreferences(
    workspaceId: number,
    preferences: GroupPreference,
  ): Promise<StoredGroupPreference> {
    const response = await apiClient.post<StoredGroupPreference>(
      `${PREFERENCES_ENDPOINT}/workspace/${workspaceId}`,
      preferences,
    );
    // The response IS the data (not response.data)
    return response;
  },

  /**
   * Batch update all preferences for a workspace
   */
  async batchUpdatePreferences(
    workspaceId: number,
    preferences: GroupPreference[],
  ): Promise<PreferencesUpdateResult> {
    logger.debug("[preferencesService] batchUpdatePreferences called:", {
      workspaceId,
      preferences,
      url: `${PREFERENCES_ENDPOINT}/workspace/${workspaceId}/batch`,
    });

    try {
      const response = await apiClient.put<PreferencesUpdateResult>(
        `${PREFERENCES_ENDPOINT}/workspace/${workspaceId}/batch`,
        preferences,
      );
      logger.debug(
        "[preferencesService] batchUpdatePreferences response:",
        response,
      );

      // The response IS the data (not response.data)
      return response;
    } catch (error) {
      logger.error(
        "[preferencesService] batchUpdatePreferences error:",
        error,
      );
      throw error;
    }
  },

  /**
   * Delete all user preferences for a workspace
   */
  async deleteWorkspacePreferences(
    workspaceId: number,
  ): Promise<PreferencesUpdateResult> {
    const response = await apiClient.delete<PreferencesUpdateResult>(
      `${PREFERENCES_ENDPOINT}/workspace/${workspaceId}`,
    );
    // The response IS the data (not response.data)
    return response;
  },

  /**
   * Apply preferences to a list of groups
   */
  applyPreferencesToGroups<T extends { group_id?: number; id?: number }>(
    groups: T[],
    preferences: GroupPreference[],
  ): GroupWithPreferences<T>[] {
    // Defensive programming: ensure groups is an array
    if (!Array.isArray(groups)) {
      logger.error(
        "[preferencesService] applyPreferencesToGroups called with non-array:",
        {
          groups,
          groupsType: typeof groups,
          isFunction: typeof groups === "function",
          groupsString: String(groups).substring(0, 200) + "...",
        },
      );
      return [];
    }

    // Defensive programming: without a preferences array, apply none (every
    // group expanded, in its current order).
    if (!Array.isArray(preferences)) {
      logger.error(
        "[preferencesService] applyPreferencesToGroups called with non-array preferences:",
        preferences,
      );
      preferences = [];
    }

    // Create a map of preferences by group_id
    const prefMap = new Map(preferences.map((pref) => [pref.group_id, pref]));

    // Map groups with their preferences
    const groupsWithPrefs: GroupWithPreferences<T>[] = groups.map(
      (group, index) => {
        // Handle both group_id and id fields
        const groupId = group.group_id ?? group.id;
        const pref = groupId === undefined ? undefined : prefMap.get(groupId);

        return {
          ...group,
          expanded: pref ? pref.expanded : true,
          // Use saved position if available, otherwise use current array index to maintain order
          position: pref ? pref.position : index,
        };
      },
    );

    // Sort by position
    return groupsWithPrefs.sort((a, b) => a.position - b.position);
  },
};
